import {
  type FC,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef
} from "react";
import { useBlocNext } from "@blac/react";
import { Button } from "@9amhealth/shared";
import LeapChatCubit from "./LeapChatCubit";
import { HeroInputCard, HeroInput, HeroToolbar } from "./leapChat.styles";

const PlusIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ArrowUpIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

interface ChatComposerProps {
  onSend: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const ChatComposer: FC<ChatComposerProps> = ({
  onSend,
  placeholder = "Ask about your health, medications, or results…",
  autoFocus
}) => {
  const [{ inputValue, isRunning }, cubit] = useBlocNext(LeapChatCubit);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!inputValue) {
      const el = textareaRef.current;
      if (el) {
        el.style.height = "auto";
      }
    }
  }, [inputValue]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isRunning && inputValue.trim()) {
          onSend();
        }
      }
    },
    [isRunning, inputValue, onSend]
  );

  return (
    <HeroInputCard className="chat-composer">
      <HeroInput
        ref={textareaRef}
        rows={1}
        value={inputValue}
        onChange={(e) => {
          cubit.setInputValue(e.target.value);
          autoResize();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isRunning}
      />
      <HeroToolbar>
        <Button
          theme="subtle"
          outline
          round
          hideArrow
          size="compact"
          icon={<PlusIcon />}
          aria-label="Add attachment"
        />
        <Button
          theme="subtle"
          round
          hideArrow
          size="compact"
          icon={<ArrowUpIcon />}
          onPress={onSend}
          isDisabled={isRunning || !inputValue.trim()}
          aria-label="Send message"
        />
      </HeroToolbar>
    </HeroInputCard>
  );
};

export default ChatComposer;
